import gsap from 'gsap';
import { AdditiveBlending, Object3D, Vector2, Vector3 } from 'three';
import { appMixin } from '../../../utils/appMixin';
import { applyInstances } from '../../../utils/misc';
import { MapMaterial } from '../../Materials/Map/material';
import { StarsMaterial } from '../../Materials/Stars/material';

export class Arena extends appMixin(Object3D) {
  constructor(app) {
    super(app);

    this.materials = [];

    this.commonUniforms = {
      uVisiblity: { value: 0 },
    };
  }

  disposeArena = () => {
    this.materials.forEach((material) => {
      material.uniforms.tMap.value?.dispose();
      material.uniforms.tAo.value?.dispose();
      material.dispose();
    });

    this.traverse((o) => {
      if (o.isMesh) o.geometry?.dispose();
    });

    this.clear();
  };

  updateModel = (object) => {
    if (this.first) this.first = false;
    else this.disposeArena();

    // applyInstances(object)

    object.traverse((e) => {
      if (e.userData.isStars) {
        e.material = new StarsMaterial({
          uniforms: {
            ...this.app.uniforms,
            ...this.commonUniforms,
            tNoise: { value: this.app.tools.noise.texture },
            tLinearNoise: { value: this.app.tools.noise.linearTexture },
          },
        });
      } else if (e.material) {
        e.material = new MapMaterial(this.app, {
          uniforms: {
            ...this.app.uniforms,
            ...this.commonUniforms,
            uCoolEffectIntensity: this.app.coolEffectIntensity,
            tMap: { value: e.material.map },
            tAo: { value: e.material.emissiveMap },
            tNoise: { value: this.app.tools.noise.texture },
            uIntensity: {
              value: typeof e.userData.intensity === 'number' ? e.userData.intensity : 1,
            },
            uMaterialColor: {
              value: e.material.color,
            },
            uColorInfluence: {
              value: typeof e.userData.colorInfluence === 'number' ? e.userData.colorInfluence : 0,
            },
            uSaturation: {
              value: typeof e.userData.saturation === 'number' ? e.userData.saturation : 1,
            },
            uHueShift: {
              value: typeof e.userData.hueShift === 'number' ? e.userData.hueShift : 0,
            },
            uMapAnimation: {
              value: typeof e.userData.mapAnimation === 'number' ? e.userData.mapAnimation : 0,
            },
            uMeshAnimation: {
              value: typeof e.userData.meshAnimation === 'number' ? e.userData.meshAnimation : 0,
            },
            uMeshSpeed: {
              value: typeof e.userData.meshSpeed === 'number' ? e.userData.meshSpeed : 1,
            },
            uMeshDirection: {
              value:
                typeof e.userData.meshDirection === 'object'
                  ? new Vector3(
                      e.userData.meshDirection[0],
                      e.userData.meshDirection[1],
                      e.userData.meshDirection[2]
                    )
                  : new Vector3(1, 1, 1),
            },
            uUvScale: {
              value:
                typeof e.userData.uvScale === 'object'
                  ? new Vector2(e.userData.uvScale[0], e.userData.uvScale[1])
                  : new Vector2(1, 1),
            },
            uMapDirection: {
              value:
                typeof e.userData.mapDirection === 'object'
                  ? new Vector2(e.userData.mapDirection[0], e.userData.mapDirection[1])
                  : new Vector2(1, 1),
            },
            uAoIntensity: {
              value: typeof e.userData.aoIntensity === 'number' ? e.userData.aoIntensity : 0,
            },
            uAoLuminosity: {
              value: typeof e.userData.aoLuminosity === 'number' ? e.userData.aoLuminosity : 0,
            },
            uMaterialColorInflence: {
              value:
                typeof e.userData.materialColorInflence === 'number'
                  ? e.userData.materialColorInflence
                  : 0,
            },
            uMapContrast: {
              value: typeof e.userData.mapContrast === 'number' ? e.userData.mapContrast : 1,
            },
            uAoContrast: {
              value: typeof e.userData.aoContrast === 'number' ? e.userData.aoContrast : 1,
            },
          },
        });

        if (object.name === 'city' || object.name === 'spaceship')
          e.material.defines = { ...e.material.defines, USE_UV_FOR_DISPLACEMENT: true };

        if (e.userData.blending === 'additive') {
          e.material.blending = AdditiveBlending;
          e.material.depthWrite = false;
          e.renderOrder = 2;
        }

        e.material.userData.name = e.name;
        this.materials.push(e.material);
      }
    });

    this.add(object);
  };

  open() {
    this.tl?.kill();
    this.tl = gsap.timeline();

    this.tl.set(this, { visible: true });
    this.tl.to(this.commonUniforms.uVisiblity, { value: 1, duration: 1.5, ease: 'power3.out' }, 0);

    return this.tl;
  }

  close() {
    this.tl?.kill();
    this.tl = gsap.timeline();

    this.tl.to(this.commonUniforms.uVisiblity, { value: 0, duration: 1, ease: 'sine.inOut' }, 0);
    this.tl.set(this, { visible: false });

    return this.tl;
  }
}
